import React from "react";
import CameraReport from "./CameraReport";
import Typography from "@mui/material/Typography";
import { titleCase } from "title-case";
import { CardActionArea, CardMedia } from "@mui/material";
import Carousel from "react-material-ui-carousel";
import Box from "@mui/material/Box";
import VideoPlayer from "../../../components/VideoPlayer";
import "../../../App.css";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { copyText } from "../../../util/ReportUtil";
import { useIsAdmin } from "../../../util/AdminUtil";
import { imageDirectionsPresent, directionLabel } from "./CameraDirections";
import {
  resolveLastUpdated,
  describeAge,
  STALE_THRESHOLD_MS,
  useAgeTicker,
} from "../../../util/CameraAgeUtil";

const DescriptionBlock = (props) => {
  return (
    <Typography
      variant="subtitle1"
      sx={{ width: "99%", textAlign: "center", pr: 5, pl: 5 }}
    >
      Click on the location to see a map of the camera
    </Typography>
  );
};

const MyCardHeader = (props) => {
  const isDebug = useIsAdmin();

  const d = props.d;
  const url = `/map?lat=${d.latitude}&lng=${d.longitude}&type=Cameras`;
  return (
    <Box sx={{ textAlign: "center" }}>
      <Link to={url} target="_blank" className="reportCell">
        <span dangerouslySetInnerHTML={{ __html: d.agency }} />
        <span>:&nbsp;</span>
        <span>{titleCase(d.location)} </span>
      </Link>
      {isDebug && (
        <div title="Click to Copy">
          <Typography
            variant="body2"
            onClick={() => copyText(d.externalId.trim())}
          >
            <span className="hover-highlight">{d.externalId.trim()}</span>
          </Typography>
        </div>
      )}
    </Box>
  );
};

const ImageSource = (origImgUrl) => {
  var imgSrc;
  if (origImgUrl != null && origImgUrl.includes("http")) {
    imgSrc = origImgUrl;
  } else {
    imgSrc = process.env.REACT_APP_API_HOST + "/" + origImgUrl;
  }
  return imgSrc;
};

const MyCardMedia = (props) => {
  useAgeTicker();
  const [searchParams] = useSearchParams();
  const isShowOld = searchParams.has("showOld");
  var d = props.d;
  var imageUrls = [];
  var oldImageUrls = [];
  var directionList = [];
  var oldImageDirectionList = [];
  var ageList = [];
  var oldImageAgeList = [];
  //New Logic with Direction to pull
  if (d.imageDirections != null && d.singleView !== true) {
    imageDirectionsPresent(d.imageDirections).forEach(({ key, label }) => {
      const image = d.imageDirections[key];
      directionList.push(label);
      ageList.push(resolveLastUpdated(image));
      imageUrls.push(ImageSource(image.url));
    });
  } else {
    imageUrls.push(ImageSource(d.url));
    ageList.push(
      resolveLastUpdated({ lastUpdated: d.lastUpdated, imageAge: d.imageAge }),
    );
    const label = directionLabel(d.direction);
    if (label) directionList.push(label);
  }

  const showOldPresenceCheck = (ageList) => {
    var showOldPresenceCheck = false;
    for (var i = 0; i < ageList.length; i++)
      if (describeAge(ageList[i]).ms > STALE_THRESHOLD_MS) {
        oldImageUrls.push(imageUrls[i]);
        oldImageAgeList.push(ageList[i]);
        oldImageDirectionList.push(directionList[i]);
        showOldPresenceCheck = true;
      }
    return showOldPresenceCheck;
  };

  const handleImagesNewWindow = (
    event,
    clickedIndex,
    allUrls,
    allDirections,
    allAges,
  ) => {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const left = (screenWidth - 900) / 2;
    const top = (screenHeight - 700) / 2;

    const newWindow = window.open(
      "",
      "_blank",
      `scrollbars,width=900,height=700,left=${left},top=${top}`,
    );

    const fullUrls = allUrls.map((u) =>
      u.replace("camera?type=thumbnail", "snapshot?"),
    );
    const urlsJson = JSON.stringify(fullUrls);
    const directionsJson = JSON.stringify(allDirections);
    const agesJson = JSON.stringify(allAges);

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Camera Image</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
            width: 100%;
            height: 100%;
            overflow: hidden;
        }
        body {
            background: #1a1a1a;
            color: #f0f0f0;
            font-family: Arial, sans-serif;
            display: flex;
            flex-direction: column;
        }
        .img-container {
            flex: 1;
            min-height: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
        }
        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
        .direction-buttons {
            display: flex;
            gap: 8px;
            padding: 6px 10px;
            flex-wrap: wrap;
            justify-content: center;
            background: #111;
            border-top: 1px solid #333;
            flex-shrink: 0;
        }
        .dir-btn {
            background: #2a2a2a;
            color: #f0f0f0;
            border: 1px solid #555;
            border-radius: 6px;
            padding: 5px 20px;
            font-size: 0.9rem;
            cursor: pointer;
            transition: background 0.15s, border-color 0.15s;
        }
        .dir-btn:hover {
            background: #3a3a3a;
            border-color: #90caf9;
        }
        .dir-btn.active {
            background: #1565c0;
            border-color: #90caf9;
            color: #fff;
            font-weight: bold;
        }
        .age-label {
            align-self: center;
            font-size: 0.8rem;
            color: #aaa;
            margin-left: 12px;
        }
    </style>
</head>
<body>
    <div class="img-container">
        <img id="cam-img" src="" alt="Camera Image" />
    </div>
    <div class="direction-buttons" id="dir-buttons">
        <span class="age-label" id="age-label"></span>
    </div>
    <script>
        const urls = ${urlsJson};
        const directions = ${directionsJson};
        const ages = ${agesJson};

        function show(index) {
            document.getElementById('cam-img').src = urls[index];
            document.getElementById('age-label').textContent = ages[index] || '';
            document.title = 'Camera Image' + (directions[index] ? ' \u2013 ' + directions[index] : '');
            document.querySelectorAll('.dir-btn').forEach(function(btn, i) {
                btn.classList.toggle('active', i === index);
            });
        }

        const container = document.getElementById('dir-buttons');
        directions.forEach(function(dir, i) {
            const btn = document.createElement('button');
            btn.className = 'dir-btn';
            btn.textContent = dir;
            btn.onclick = function() { show(i); };
            container.appendChild(btn);
        });

        show(${clickedIndex});
    </script>
</body>
</html>`;

    newWindow.document.write(html);
    newWindow.document.close();
    newWindow.focus();
  };

  // Render directly so timer and report updates preserve the media components.
  return (
    <div sx={{ height: "100%" }}>
      {d.videoUrl && (
        <Box className="video-report" sx={{ mb: 1 }}>
          <VideoPlayer streamUrl={d.videoUrl} posterUrl={imageUrls[0]} />
        </Box>
      )}
      {isShowOld === false && (
        <Carousel
          sx={{ height: "100%" }}
          interval={null}
          navButtonsProps={{
            size: "small",
            style: {
              borderRadius: 10,
              marginLeft: -10,
              marginRight: -10,
              marginTop: 0,
            },
          }}
          navButtonsAlwaysVisible={imageUrls.length > 1}
          navButtonsAlwaysInvisible={imageUrls.length === 1}
          indicators={false}
        >
          {imageUrls.map((item, i) => {
            return (
              <Box
                onClick={(e) =>
                  handleImagesNewWindow(
                    e,
                    i,
                    imageUrls,
                    directionList,
                    ageList.map(
                      (lastUpdated) => describeAge(lastUpdated).text,
                    ),
                  )
                }
                sx={{ display: "flex", flexDirection: "column" }}
                key={i}
              >
                <CardActionArea
                  sx={{ textAlign: "center", border: "none", mb: 1 }}
                >
                  {describeAge(ageList[i]).text}
                </CardActionArea>
                <CardMedia
                  component="img"
                  alt="Camera Image"
                  image={item}
                  access-control-allow-origin="*"
                  referrerPolicy="no-referrer"
                  sx={{
                    minHeight: " 200px ",
                    objectFit: "contain",
                    backgroundSize: "contain",
                    verticalAlign: "top",
                    objectPosition: "center top",
                    textAlign: "center",
                  }}
                />
                <CardActionArea
                  sx={{ textAlign: "center", border: "none", mt: 2 }}
                >
                  {directionList[i]}
                </CardActionArea>
              </Box>
            );
          })}
        </Carousel>
      )}
      {isShowOld === true && showOldPresenceCheck(ageList) === true && (
        <Carousel
          sx={{ height: "100%" }}
          interval={null}
          navButtonsProps={{
            size: "small",
            style: {
              borderRadius: 10,
              marginLeft: -10,
              marginRight: -10,
              marginTop: 0,
            },
          }}
          navButtonsAlwaysVisible={oldImageUrls.length > 1}
          navButtonsAlwaysInvisible={oldImageUrls.length === 1}
          indicators={false}
        >
          {oldImageUrls.map((item, i) => {
            return (
              <Box
                onClick={(e) =>
                  handleImagesNewWindow(
                    e,
                    i,
                    oldImageUrls,
                    oldImageDirectionList,
                    oldImageAgeList.map(
                      (lastUpdated) => describeAge(lastUpdated).text,
                    ),
                  )
                }
                sx={{ display: "flex", flexDirection: "column" }}
                key={i}
              >
                <CardActionArea
                  sx={{ textAlign: "center", border: "none", mb: 1 }}
                >
                  {describeAge(oldImageAgeList[i]).text}
                </CardActionArea>
                <CardMedia
                  component="img"
                  alt="Camera Image"
                  image={item}
                  access-control-allow-origin="*"
                  referrerPolicy="no-referrer"
                  sx={{
                    minHeight: "200px",
                    objectFit: "contain",
                    backgroundSize: "contain",
                    verticalAlign: "top",
                    objectPosition: "center top",
                    textAlign: "center",
                  }}
                />
                <CardActionArea
                  sx={{ textAlign: "center", border: "none", mt: 2 }}
                >
                  {oldImageDirectionList[i]}
                </CardActionArea>
              </Box>
            );
          })}
        </Carousel>
      )}
    </div>
  );
};

const module = {
  routeProps: {
    path: "CameraReport",
    component: CameraReport,
  },
  name: "Camera",
  label: "Camera",
  title: "Camera Report",
  showOnMenu: true,
  endpoint: "/cameraReport.json",
  filterEndpoint: "/cameraReportLocations.json",
  cardHeader: MyCardHeader,
  cardMedia: MyCardMedia,
  descriptionBlock: DescriptionBlock,
  cardColumns: [],
};

export default module;
